<template>
  <div
    class="schedule-group"
    :class="[groupTypeClass, { selected: isSelected, 'future-date': isFutureDate }]"
    @click="hasRadio ? selectGroup() : navigateToGroupAttendance()"
  >
    <div class="schedule-group__header">
      <input type="radio" :checked="isSelected" v-if="hasRadio" />
      <p
        class="schedule-group__header-group-name"
        @click.stop="navigateToGroupAttendance"
      >
        {{ group.name }}
      </p>
      <span class="schedule-group__header-date">{{ shortDate }}</span>
    </div>
    <div class="schedule-group__footer">
      <p class="schedule-group__footer-teacher">
        {{ group.user?.first_name }} {{ group.user?.last_name }}
      </p>
      <span
        v-if="group.students_count !== null && group.students_count !== undefined"
        class="schedule-group__footer-students"
        :class="studentsCountClass"
        :title="`O'quvchilar soni: ${group.students_count}`"
        :aria-label="`O'quvchilar soni: ${group.students_count}`"
      >
        {{ group.students_count }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { DateTime } from "luxon";
import { computed } from "vue";
import { useRouter } from "vue-router";
import store from "@/store";

const router = useRouter();

const props = defineProps({
  group: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  hasRadio: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["group-selected"]);

const selectGroup = () => {
  emit("group-selected", props.group);
  // Update the store with the selected group ID
  store.dispatch("setTeacherActiveGrId", props.group.id);
};

const navigateToGroupAttendance = () => {
  // Update the store with the selected group ID before navigating
  store.dispatch("setTeacherActiveGrId", props.group.id);
  const courseId = props.group?.category_id || props.group?.category?.id;
  if (courseId) {
    store.dispatch("setTeacherActiveGrCourseId", courseId);
  }
  router.push(`/group-attendance/${props.group.id}`);
};

const groupTypeClass = computed(() => {
  if (props.group.name.toLowerCase().includes("front")) {
    return "front";
  } else if (props.group.name.toLowerCase().includes("back")) {
    return "back";
  } else {
    return "other";
  }
});

const isFutureDate = computed(() => {
  if (!props.group.date_started) return false;
  const startDate = DateTime.fromISO(props.group.date_started);
  const now = DateTime.now();
  return startDate > now;
});

const shortDate = computed(() => {
  if (!props.group.date_started) return "";
  return DateTime.fromISO(props.group.date_started)
    .setLocale("ru")
    .toFormat("LLL''yy");
});

const studentsCountClass = computed(() => {
  const n = props.group.students_count;
  if (n === null || n === undefined) return "";
  if (n < 7) return "students-red";
  if (n <= 10) return "students-yellow";
  if (n <= 16) return "students-green";
  return "students-blue";
});
</script>

<style lang="scss">
.schedule-group {
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  min-width: 0;
  overflow: hidden;
  line-height: 1.15;

  &.selected {
    border-width: 2px;
  }

  &.front {
    background-color: #dffbdd;
    border: 0.4px solid #8dec89;
  }

  &.back {
    background-color: #fafcc5;
    border: 0.4px solid #f8ef4c;
  }

  &.other {
    background-color: #fef2f2;
    border: 0.4px solid #f57474;
  }

  .schedule-group__header {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;

    .schedule-group__header-group-name {
      font-size: 12px;
      font-weight: 600;
      color: #262626;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
      flex: 1;
    }

    .schedule-group__header-date {
      font-size: 10px;
      font-weight: 500;
      color: #888888;
      text-transform: lowercase;
      flex-shrink: 0;
    }
  }

  .schedule-group__footer {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    min-width: 0;

    .schedule-group__footer-teacher {
      font-size: 10px;
      font-weight: 500;
      color: #4f4f4f;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
    }

    .schedule-group__footer-students {
      font-size: 10px;
      font-weight: 700;
      padding: 0 5px;
      border-radius: 3px;
      flex-shrink: 0;
      cursor: help;

      &.students-red {
        background-color: #fee2e2;
        color: #b91c1c;
      }
      &.students-yellow {
        background-color: #fef3c7;
        color: #b45309;
      }
      &.students-green {
        background-color: #dcfce7;
        color: #15803d;
      }
      &.students-blue {
        background-color: #dbeafe;
        color: #1d4ed8;
      }
    }
  }

  &.front .schedule-group__body-duration {
    background-color: #c0f5bd;
    color: #1b7d18;
  }

  &.back .schedule-group__body-duration {
    background-color: #fbf98d;
    color: #9d710b;
  }

  &.other .schedule-group__body-duration {
    background-color: #fdcbcb;
    color: #b52020;
  }

  &.front .schedule-group__body-students-count {
    background-color: #b3e5fc;
    color: #0277bd;
  }

  &.back .schedule-group__body-students-count {
    background-color: #fff9c4;
    color: #f57f17;
  }

  &.other .schedule-group__body-students-count {
    background-color: #ffccbc;
    color: #d84315;
  }

  &.future-date {
    background-color: #e5e5e5 !important;
    border-color: #b0b0b0 !important;
    opacity: 0.7;

    .schedule-group__body-duration {
      background-color: #f0f0f0 !important;
      color: #888888;
    }

    .schedule-group__body-students-count {
      background-color: #f0f0f0 !important;
      color: #888888;
    }
  }
}
</style>
